// Package imports
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// CSS imports
import './CreateBlogPost.css';

// Component imports
import Alert from '../../components/alert/Alert';
import FullPageSpinner from '../../components/fullPageSpinner/FullPageSpinner';
import TextInput from '../../components/inputs/textInput/TextInput';
import TextEditor from '../../components/textEditor/editor/TextEditor';
import PrimaryButton from '../../components/buttons/primaryButton/PrimaryButton';

// Helper imports
import SectionWrapper from '../../helpers/components/sectionWrapper/SectionWrapper';

// Service imports
import { ApiService } from '../../services/ApiService';

// Icon imports
import { MdError } from 'react-icons/md';

// Type imports
import { AlertSize } from '../../types/alert/AlertSize';
import { SectionContentSize } from '../../types/sectionWrapper/SectionContentSize';
import { BlogLinkResult } from '../../types/blog/BlogLinkResult';

const CreateBlogPost: React.FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');

  const [titleError, setTitleError] = useState('');
  const [contentError, setContentError] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const [showUnknownError, setShowUnknownError] = useState(false);

  const [content, setContent] = useState('');

  const onChange = (e: any) => {
    switch (e.target.name) {
      case 'title': {
        setTitle(e.target.value);
        setTitleError('');
        break;
      }
    }
  };

  const triggerUpload = () => {
    const isValid = checkInputs();

    if (isValid) {
      uploadPost();
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const checkInputs = () => {
    let isValid = true;
    if (title === '') {
      setTitleError('Please enter a title.');
      isValid = false;
    }

    if (content === '' || content === '<p></p>') {
      setContentError('Please enter your article.');
      isValid = false;
    }

    return isValid;
  };

  const uploadPost = async () => {
    setIsLoading(true);

    const res: BlogLinkResult | null = await ApiService.createBlogPost(
      title,
      content
    );

    setIsLoading(false);

    if (res) {
      navigate('/blog');
    } else {
      setShowUnknownError(true);
    }
  };

  return (
    <div className='CreateBlogPost'>
      <SectionWrapper contentWidth={SectionContentSize.SMALL}>
        <div className='content'>
          <div className='header-container'>
            <h1>Create Blog Post</h1>
          </div>

          <TextInput
            value={title}
            name='title'
            placeholder='Title'
            onChange={onChange}
            errorMessage={titleError}
            className='title-text-input'
          />
          <TextEditor
            content={content}
            setContent={setContent}
            setContentError={setContentError}
          />
          {contentError ? (
            <span className='invalid-input-message'>{contentError}</span>
          ) : null}
          <div className='publish-button-container'>
            <PrimaryButton label='Publish' onClick={triggerUpload} />
          </div>
        </div>
      </SectionWrapper>
      <Alert
        showAlert={showUnknownError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowUnknownError}
      >
        <MdError size={20} />
        <span>Something went wrong!</span>
      </Alert>
      {isLoading ? <FullPageSpinner /> : null}
    </div>
  );
};

export default CreateBlogPost;
